import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const TravelDocuments = ({ data, onUpdate, isEditing, setIsEditing }) => {
  const [localData, setLocalData] = useState(data);
  const [errors, setErrors] = useState({});

  const handleEdit = () => {
    setLocalData(data);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setLocalData(data);
    setIsEditing(false);
    setErrors({});
  };

  const handleSave = () => {
    // Basic validation
    const newErrors = {};
    if (!localData?.passport?.number?.trim()) {
      newErrors.passportNumber = 'Passport number is required';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdate(localData);
    setIsEditing(false);
    setErrors({});
  };

  const updatePassport = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      passport: {
        ...prev?.passport,
        [field]: value
      }
    }));
  };

  const updateInsurance = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      travelInsurance: {
        ...prev?.travelInsurance,
        [field]: value
      }
    }));
  };

  const addVisa = () => {
    const newVisa = {
      id: `visa-${Date.now()}`,
      country: '',
      type: '',
      validFrom: '',
      validUntil: '',
      status: 'pending'
    };
    setLocalData(prev => ({
      ...prev,
      visas: [...(prev?.visas || []), newVisa]
    }));
  };

  const updateVisa = (index, field, value) => {
    setLocalData(prev => ({
      ...prev,
      visas: prev?.visas?.map((visa, i) => 
        i === index ? { ...visa, [field]: value } : visa
      )
    }));
  };

  const removeVisa = (index) => {
    setLocalData(prev => ({
      ...prev,
      visas: prev?.visas?.filter((_, i) => i !== index)
    }));
  };

  const addVaccination = () => {
    const newVaccination = {
      id: `vac-${Date.now()}`,
      name: '',
      date: '',
      validUntil: '',
      certificate: false
    };
    setLocalData(prev => ({
      ...prev,
      vaccinations: [...(prev?.vaccinations || []), newVaccination]
    }));
  };

  const updateVaccination = (index, field, value) => {
    setLocalData(prev => ({
      ...prev,
      vaccinations: prev?.vaccinations?.map((vac, i) => 
        i === index ? { ...vac, [field]: value } : vac
      )
    }));
  };

  const removeVaccination = (index) => {
    setLocalData(prev => ({
      ...prev,
      vaccinations: prev?.vaccinations?.filter((_, i) => i !== index)
    }));
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'unknown', message: 'No expiry date' };
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', message: 'Expired', color: 'text-destructive' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'warning', message: `Expires in ${daysUntilExpiry} days`, color: 'text-warning' };
    } else if (daysUntilExpiry <= 90) {
      return { status: 'caution', message: `Expires in ${daysUntilExpiry} days`, color: 'text-yellow-600' };
    } else {
      return { status: 'valid', message: `Valid until ${expiry?.toLocaleDateString()}`, color: 'text-success' };
    }
  };

  const StatusBadge = ({ status, message, color }) => (
    <div className={`flex items-center space-x-1 ${color}`}>
      <Icon 
        name={status === 'expired' ? 'AlertCircle' : status === 'warning' ? 'AlertTriangle' : 'CheckCircle'} 
        size={14} 
      />
      <span className="text-xs font-medium">{message}</span>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Travel Documents</h2>
          <p className="text-muted-foreground mt-1">
            Manage your passport, visas, insurance, and health certificates
          </p>
        </div>
        
        {!isEditing ? (
          <Button
            onClick={handleEdit}
            iconName="Edit"
            variant="outline"
            size="sm"
          >
            Edit
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              onClick={handleCancel}
              variant="ghost"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              iconName="Save"
              size="sm"
            >
              Save
            </Button>
          </div>
        )}
      </div>
      {/* Passport Information */}
      <div className="bg-card rounded-lg p-6 border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Book" size={20} className="mr-2" />
            Passport Information
          </h3>
          {localData?.passport?.expiryDate && (
            <StatusBadge {...getExpiryStatus(localData?.passport?.expiryDate)} />
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Passport Number"
            required
            value={localData?.passport?.number || ''}
            onChange={(e) => updatePassport('number', e?.target?.value)}
            error={errors?.passportNumber}
            disabled={!isEditing}
          />
          
          <Input
            label="Issuing Country"
            value={localData?.passport?.issuingCountry || ''}
            onChange={(e) => updatePassport('issuingCountry', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Issue Date"
            type="date"
            value={localData?.passport?.issueDate || ''}
            onChange={(e) => updatePassport('issueDate', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Expiry Date"
            type="date"
            value={localData?.passport?.expiryDate || ''}
            onChange={(e) => updatePassport('expiryDate', e?.target?.value)}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Visas */}
      <div className="bg-card rounded-lg p-6 border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Stamp" size={20} className="mr-2" />
            Visas
          </h3>
          {isEditing && (
            <Button
              onClick={addVisa}
              iconName="Plus"
              variant="outline"
              size="sm"
            >
              Add Visa
            </Button>
          )}
        </div>
        
        <div className="space-y-6">
          {localData?.visas?.map((visa, index) => (
            <div key={visa?.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-foreground">Visa #{index + 1}</h4>
                <div className="flex items-center space-x-2">
                  {visa?.validUntil && (
                    <StatusBadge {...getExpiryStatus(visa?.validUntil)} />
                  )}
                  {isEditing && (
                    <Button
                      onClick={() => removeVisa(index)}
                      variant="ghost"
                      size="sm"
                      iconName="Trash"
                    />
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Country"
                  value={visa?.country || ''}
                  onChange={(e) => updateVisa(index, 'country', e?.target?.value)}
                  disabled={!isEditing}
                />
                
                <Input
                  label="Visa Type"
                  value={visa?.type || ''}
                  onChange={(e) => updateVisa(index, 'type', e?.target?.value)}
                  disabled={!isEditing}
                />
                
                <Input
                  label="Valid From"
                  type="date"
                  value={visa?.validFrom || ''}
                  onChange={(e) => updateVisa(index, 'validFrom', e?.target?.value)}
                  disabled={!isEditing}
                />
                
                <Input
                  label="Valid Until"
                  type="date"
                  value={visa?.validUntil || ''}
                  onChange={(e) => updateVisa(index, 'validUntil', e?.target?.value)}
                  disabled={!isEditing}
                />
              </div>
            </div>
          ))}
          
          {(!localData?.visas || localData?.visas?.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="FileX" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No visas added yet</p>
            </div>
          )}
        </div>
      </div>
      {/* Travel Insurance */}
      <div className="bg-card rounded-lg p-6 border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Shield" size={20} className="mr-2" />
            Travel Insurance
          </h3>
          {localData?.travelInsurance?.validUntil && (
            <StatusBadge {...getExpiryStatus(localData?.travelInsurance?.validUntil)} />
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Insurance Provider"
            value={localData?.travelInsurance?.provider || ''}
            onChange={(e) => updateInsurance('provider', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Policy Number"
            value={localData?.travelInsurance?.policyNumber || ''}
            onChange={(e) => updateInsurance('policyNumber', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Coverage Amount"
            value={localData?.travelInsurance?.coverage || ''}
            onChange={(e) => updateInsurance('coverage', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Valid From"
            type="date"
            value={localData?.travelInsurance?.validFrom || ''}
            onChange={(e) => updateInsurance('validFrom', e?.target?.value)}
            disabled={!isEditing}
          />
          
          <Input
            label="Valid Until"
            type="date"
            value={localData?.travelInsurance?.validUntil || ''}
            onChange={(e) => updateInsurance('validUntil', e?.target?.value)}
            disabled={!isEditing}
          />
        </div>
      </div>
      {/* Health Certificates */}
      <div className="bg-card rounded-lg p-6 border">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Heart" size={20} className="mr-2" />
            Vaccinations & Health Certificates
          </h3>
          {isEditing && (
            <Button
              onClick={addVaccination}
              iconName="Plus"
              variant="outline"
              size="sm"
            >
              Add Vaccination
            </Button>
          )}
        </div>
        
        <div className="space-y-6">
          {localData?.vaccinations?.map((vaccination, index) => (
            <div key={vaccination?.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-foreground">
                  {vaccination?.name || `Vaccination #${index + 1}`}
                </h4>
                <div className="flex items-center space-x-2">
                  {vaccination?.validUntil && (
                    <StatusBadge {...getExpiryStatus(vaccination?.validUntil)} />
                  )}
                  {vaccination?.certificate && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                      <Icon name="CheckCircle" size={12} className="mr-1" />
                      Certified
                    </span>
                  )}
                  {isEditing && (
                    <Button
                      onClick={() => removeVaccination(index)}
                      variant="ghost"
                      size="sm"
                      iconName="Trash"
                    />
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Vaccination Name"
                  value={vaccination?.name || ''}
                  onChange={(e) => updateVaccination(index, 'name', e?.target?.value)}
                  disabled={!isEditing}
                />
                
                <Input
                  label="Vaccination Date"
                  type="date"
                  value={vaccination?.date || ''}
                  onChange={(e) => updateVaccination(index, 'date', e?.target?.value)}
                  disabled={!isEditing}
                />
                
                <Input
                  label="Valid Until"
                  type="date"
                  value={vaccination?.validUntil || ''}
                  onChange={(e) => updateVaccination(index, 'validUntil', e?.target?.value)}
                  disabled={!isEditing}
                />
              </div>
              
              {isEditing && (
                <div className="mt-4">
                  <label className="flex items-center space-x-2">
                    <Input
                      type="checkbox"
                      checked={vaccination?.certificate || false}
                      onChange={(e) => updateVaccination(index, 'certificate', e?.target?.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-foreground">I have a digital certificate</span>
                  </label>
                </div>
              )}
            </div>
          ))}
          
          {(!localData?.vaccinations || localData?.vaccinations?.length === 0) && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Heart" size={48} className="mx-auto mb-2 opacity-50" />
              <p>No vaccinations recorded yet</p>
            </div>
          )}
        </div>
      </div>
      {/* Document Reminders */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Clock" size={20} className="text-yellow-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-yellow-900 mb-2">Document Reminders</h4>
            <div className="text-xs text-yellow-800 space-y-1">
              <p>• Keep digital and physical copies of all travel documents</p>
              <p>• Check passport validity - many countries require 6+ months remaining</p>
              <p>• Verify visa requirements for your destinations</p>
              <p>• Ensure travel insurance covers your planned activities</p>
              <p>• Check vaccination requirements and travel health advisories</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelDocuments;